import {
  deriveMealStatus,
  failure,
  validLocalDate,
} from '../domain/nutrition.ts';

import type {
  NutritionResult,
} from '../domain/nutrition.ts';

import type {
  NutritionRepository,
} from '../ports/nutrition-repository.ts';

import type {
  HomeNutritionMeal,
  HomeNutritionSummary,
  NutritionHomeSource,
} from '../ports/home-sources.ts';

function sameContext(
  a: {
    datasetId: string;
    controlEpoch: number;
  },
  b: {
    datasetId: string;
    controlEpoch: number;
  },
) {
  return (
    a.datasetId === b.datasetId &&
    a.controlEpoch === b.controlEpoch
  );
}

export class PublicNutritionHome
implements NutritionHomeSource {
  constructor(
    private readonly repository: Pick<
      NutritionRepository,
      | 'readContext'
      | 'listPlannedMeals'
      | 'readMealResolutions'
      | 'listConsumptions'
    >,
  ) {}

  async readNutritionHome(
    localDate: string,
  ): Promise<
    NutritionResult<HomeNutritionSummary>
  > {
    if (!validLocalDate(localDate)) {
      return failure(
        'VALIDATION_ERROR',
      );
    }

    const before =
      await this.repository.readContext();

    if (!before.ok) {
      return before;
    }

    const plans =
      await this.repository
        .listPlannedMeals(
          localDate,
          localDate,
        );

    if (!plans.ok) {
      return plans;
    }

    if (
      plans.value.some(
        view =>
          !sameContext(
            view.context,
            before.value,
          ),
      )
    ) {
      return failure(
        'DATASET_STALE',
      );
    }

    const resolutions =
      await this.repository
        .readMealResolutions(
          plans.value.map(
            view =>
              view.meal.plannedMealId,
          ),
        );

    if (!resolutions.ok) {
      return resolutions;
    }

    const consumptions =
      await this.repository
        .listConsumptions(
          localDate,
          localDate,
        );

    if (!consumptions.ok) {
      return consumptions;
    }

    const after =
      await this.repository.readContext();

    if (
      !after.ok ||
      !sameContext(
        before.value,
        after.value,
      )
    ) {
      return after.ok
        ? failure(
            'DATASET_STALE',
          )
        : after;
    }

    const byPlan =
      new Map(
        resolutions.value
          .filter(
            value =>
              value.plannedMealId,
          )
          .map(
            value => [
              value.plannedMealId!,
              value,
            ],
          ),
      );

    const meals:
      HomeNutritionMeal[] = [];

    for (const view of plans.value) {
      const status =
        deriveMealStatus(
          byPlan.get(
            view.meal.plannedMealId,
          ),
        );

      if (!status.ok) {
        return status;
      }

      meals.push({
        plannedMealId:
          view.meal.plannedMealId,

        title:
          view.meal.title,

        status:
          status.value,

        ...(view.meal.trainingSessionId
          ? {
              trainingSessionId:
                view.meal.trainingSessionId,
            }
          : {}),
      });
    }

    return {
      ok: true,
      value: {
        localDate,
        meals,

        improvisedCount:
          consumptions.value.filter(
            value =>
              !value.plannedMealId,
          ).length,

        context: { ...before.value },
      },
    };
  }
}